import {
  Box,
  TextField,
  FormHelperText,
  Typography,
  InputAdornment,
} from "@mui/material";
import { formatDollarFull } from "../utilityFunctions";

const RevenueSpreadSettingsFields = ({
  revSpreadFields,
  setRevSpreadFields,
  percentTotalError,
  setPercentTotalError,
}) => {
  // Label year: if January, show previous year; otherwise current year
  const today = new Date();
  const year =
    today.getMonth() + 1 === 1 ? today.getFullYear() - 1 : today.getFullYear();

  const unearnedRevenue =
    (Number(revSpreadFields.sfdc_total_revenue) || 0) -
    (Number(revSpreadFields.sfdc_off_the_books_revenue) || 0);

  const calculateRevenue = (percent) =>
    formatDollarFull(unearnedRevenue * ((Number(percent) || 0) / 100));

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    // Convert name and value to numbers from string if possible
    const numericName = Number(name);
    const numericValue = Number(value);

    const updated = { ...revSpreadFields, revenue_allocations: revSpreadFields.revenue_allocations.map(item => item.year === numericName ? { ...item, projected_percent: numericValue } : item) };

    const percentages = updated.revenue_allocations?.map(item => Number(item?.projected_percent) || 0) || [];
    
    const anyNegative = percentages.some(percent => percent < 0);
    const sum = percentages.reduce((acc, percent) => acc + percent, 0);
    setPercentTotalError(anyNegative || sum !== 100);

    setRevSpreadFields(updated);
  };

  // Config for rendering
  const topRow = [
    {
      label: "Total Revenue",
      name: "sfdc_total_revenue",
      value: formatDollarFull(revSpreadFields.sfdc_total_revenue),
    },
    {
      label: "Earned in Prior Years",
      name: "sfdc_off_the_books_revenue",
      value: formatDollarFull(revSpreadFields.sfdc_off_the_books_revenue),
    },
    {
      label: "Unearned Revenue",
      name: "unearned_revenue",
      value: formatDollarFull(unearnedRevenue),
    },
  ];

  let bottomRow = [];
  if (revSpreadFields.revenue_allocations.length > 0) {
    bottomRow = revSpreadFields.revenue_allocations.map((allocation, index) => {
      const isLast = index === revSpreadFields.revenue_allocations.length - 1;
      return {
        label: `${allocation?.year}${isLast ? ' and Beyond' : ''} Revenue %`,
        name: `${allocation?.year}`,
        value: allocation?.projected_percent,
        helperText: `Equivalent to: ${calculateRevenue(
          allocation?.projected_percent
        )}`,
      };
    });
  }

  return (
    <Box sx={{ width: "100%" }}>
      {/* Notes */}
      <Box sx={{ maxWidth: 1400, mx: "auto", mb: 2 }}>
        <Typography sx={{ color: "text.secondary" }}>
          NOTE: Adjust values carefully. Clicking "Save" syncs them to Salesforce.
        </Typography>
        <Typography sx={{ color: "text.secondary" }}>
          Enter percentages in the appropriate year boxes to indicate the portion of
          "Unearned Revenue" expected to be recognized each year. The boxes
          must sum up to 100%.
        </Typography>
      </Box>

      {/* Upper Grid: 3 columns for read-only dollars */}
      <Box
        sx={{
          maxWidth: 1400,
          mx: "auto",
          display: "grid",
          gap: 3,
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(280px, 1fr))" },
          alignItems: "start",
          mb: 3,
        }}
      >
        {topRow.map(({ label, name, value }) => (
          <Box key={name}>
            <TextField
              label={label}
              name={name}
              value={value}
              variant="outlined"
              fullWidth
              disabled
              InputProps={{ readOnly: true }}
              InputLabelProps={{ sx: { whiteSpace: "normal" } }}
            />
          </Box>
        ))}
      </Box>

      {/* Lower Grid: 4 columns for editable percentages */}
      <Box
        sx={{
          maxWidth: 1400,
          mx: "auto",
          display: "grid",
          gap: 3,
          gridTemplateColumns: { xs: "1fr", md: "repeat(4, minmax(280px, 1fr))" },
          alignItems: "start",
        }}
      >
        {bottomRow.map(({ label, name, value, helperText }) => (
          <Box key={name}>
            <TextField
              label={label}
              name={name}
              value={value}
              onChange={handleFieldChange}
              variant="outlined"
              fullWidth
              type="number"
              InputProps={{
                inputProps: { min: -100, max: 100 },
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              disabled={unearnedRevenue <= 0}
              InputLabelProps={{ sx: { whiteSpace: "normal" } }}
            />
            <FormHelperText>{helperText}</FormHelperText>
          </Box>
        ))}
      </Box>

      {/* Error message */}
      {percentTotalError && (
        <Typography sx={{ color: "error.main", mt: 2, textAlign: "center" }}>
          Error: Revenue spread percentages must be positive and must add up to 100%.
        </Typography>
      )}
    </Box>
  );
};

export default RevenueSpreadSettingsFields;
